import React, {useEffect, useRef, useState} from 'react';
import {Platform} from 'react-native';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import Tooltip from '@/Atoms/Tooltip';
import CCText from '@/Atoms/CCText';

const POLL_INTERVAL_MS = 5000;
const GREEN_MAX_MS = 150;
const YELLOW_MAX_MS = 300;

const COLOR_GREEN = '#1ECF52';
const COLOR_YELLOW = '#F5A623';
const COLOR_RED = '#E0364B';
const COLOR_INACTIVE = '#C9CED4';

const PULSE_KEYFRAMES_ID = 'ping-indicator-pulse-keyframes';
const PULSE_KEYFRAMES = `
@keyframes ping-indicator-pulse {
  0%   { transform: scale(1); }
  50%  { transform: scale(1.18); }
  100% { transform: scale(1); }
}
`;

type Bucket = 'good' | 'fair' | 'poor' | 'offline' | 'unavailable';
const BUCKET_SEVERITY: Record<Bucket, number> = {
  unavailable: -1,
  good: 0,
  fair: 1,
  poor: 2,
  offline: 3,
};

const POOR_EFFECTIVE_TYPES = ['slow-2g', '2g'];
const FAIR_EFFECTIVE_TYPES = ['3g'];

function readConnection(): any {
  const nav = navigator as any;
  return nav.connection || nav.mozConnection || nav.webkitConnection || null;
}

type Reading = {
  rtt: number | null;
  effectiveType: string | null;
  online: boolean;
};

function readNetwork(): Reading {
  const c = readConnection();
  const online =
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
      ? navigator.onLine
      : true;
  return {
    rtt: c && typeof c.rtt === 'number' ? c.rtt : null,
    effectiveType:
      c && typeof c.effectiveType === 'string' ? c.effectiveType : null,
    online,
  };
}

function statusForReading(reading: Reading): {
  color: string;
  activeBars: number;
  bucket: Bucket;
} {
  if (!reading.online) {
    return {color: COLOR_INACTIVE, activeBars: 0, bucket: 'offline'};
  }

  const {rtt, effectiveType} = reading;

  if (effectiveType && POOR_EFFECTIVE_TYPES.includes(effectiveType)) {
    return {color: COLOR_RED, activeBars: 1, bucket: 'poor'};
  }

  if (rtt === null) {
    return {color: COLOR_INACTIVE, activeBars: 0, bucket: 'unavailable'};
  }

  if (rtt >= YELLOW_MAX_MS) {
    return {color: COLOR_RED, activeBars: 1, bucket: 'poor'};
  }

  const forcedFair =
    effectiveType !== null && FAIR_EFFECTIVE_TYPES.includes(effectiveType);
  if (rtt >= GREEN_MAX_MS || forcedFair) {
    return {color: COLOR_YELLOW, activeBars: 2, bucket: 'fair'};
  }

  return {color: COLOR_GREEN, activeBars: 3, bucket: 'good'};
}

function isInClassroom(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.toLowerCase().includes('classroom');
  // return true;
}

function ensurePulseKeyframes() {
  if (Platform.OS !== 'web') return;
  if (typeof document === 'undefined') return;
  if (document.getElementById(PULSE_KEYFRAMES_ID)) return;
  const style = document.createElement('style');
  style.id = PULSE_KEYFRAMES_ID;
  style.innerHTML = PULSE_KEYFRAMES;
  document.head.appendChild(style);
}

function tooltipLabel(reading: Reading, bucket: Bucket): string {
  if (bucket === 'offline') return 'Offline';
  if (bucket === 'unavailable') return 'RTT unavailable';
  if (reading.rtt === null) return 'RTT unavailable';
  return `${reading.rtt} ms`;
}

function PingIndicator() {
  const {isMobileOrTab} = useMediaQuery();
  const [reading, setReading] = useState<Reading>(() => readNetwork());
  const [inClassroom, setInClassroom] = useState<boolean>(isInClassroom);
  const [pulseKey, setPulseKey] = useState(0);
  const prevBucketRef = useRef<Bucket | null>(null);

  useEffect(() => {
    ensurePulseKeyframes();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;

    const update = () => {
      setReading(readNetwork());
      setInClassroom(isInClassroom());
    };
    update();
    const id = setInterval(update, POLL_INTERVAL_MS);

    const connection = readConnection();
    const onChange = () => update();
    connection?.addEventListener?.('change', onChange);
    window.addEventListener('online', onChange);
    window.addEventListener('offline', onChange);

    return () => {
      clearInterval(id);
      connection?.removeEventListener?.('change', onChange);
      window.removeEventListener('online', onChange);
      window.removeEventListener('offline', onChange);
    };
  }, []);

  const {color, activeBars, bucket} = statusForReading(reading);

  useEffect(() => {
    const prev = prevBucketRef.current;
    if (
      prev !== null &&
      BUCKET_SEVERITY[bucket] > BUCKET_SEVERITY[prev] &&
      BUCKET_SEVERITY[bucket] > 0
    ) {
      setPulseKey(k => k + 1);
    }
    prevBucketRef.current = bucket;
  }, [bucket]);

  if (Platform.OS !== 'web') return null;
  if (isMobileOrTab) return null;
  if (!inClassroom) return null;

  const label = tooltipLabel(reading, bucket);
  const tooltipBody = (
    <CCText color={color} fontSize={10} fontWeight="semibold">
      {label}
    </CCText>
  );

  return (
    <Tooltip
      key={`${bucket}-${reading.rtt ?? 'na'}`}
      position="bottom"
      body={tooltipBody}
      backgroundColor="#FFFFFF"
      borderColor={color}
      arrowColor="#FFFFFF"
      paddingVertical={4}
      width={90}>
      {(toggle: () => void) => (
        <div
          onClick={toggle}
          onMouseEnter={toggle}
          onMouseLeave={toggle}
          aria-label={
            bucket === 'offline'
              ? 'Offline'
              : reading.rtt === null
              ? 'RTT unavailable'
              : `Network RTT ${reading.rtt}ms`
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            marginLeft: 10,
            padding: 4,
            borderRadius: 6,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: color,
            backgroundColor: `${color}14`,
            cursor: 'default',
            boxSizing: 'border-box',
          }}>
          <span
            key={pulseKey}
            style={{
              display: 'inline-flex',
              alignItems: 'flex-end',
              gap: 2,
              animation:
                pulseKey === 0
                  ? undefined
                  : 'ping-indicator-pulse 450ms ease-out',
            }}>
            {[0, 1, 2].map(i => (
              <span
                key={i}
                style={{
                  display: 'inline-block',
                  width: 4,
                  height: 6 + i * 4,
                  borderRadius: 1,
                  backgroundColor: i < activeBars ? color : COLOR_INACTIVE,
                }}
              />
            ))}
          </span>
        </div>
      )}
    </Tooltip>
  );
}

export default PingIndicator;
